import React, { useState, useEffect, useCallback } from 'react';
import { orderAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_OPTIONS = ['pending','processing','shipped','delivered','cancelled'];
const STATUS_COLORS = { pending: '#f59e0b', processing: '#3b82f6', shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444' };

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await orderAPI.getAll(params);
      setOrders(data.orders);
      setPages(data.pages);
      setTotal(data.total);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusUpdate = async (orderId, orderStatus) => {
    setUpdatingStatus(orderId);
    try {
      await orderAPI.updateStatus(orderId, { orderStatus, trackingNumber: trackingInputs[orderId] || undefined });
      toast.success('Order status updated');
      fetchOrders();
    } catch { toast.error('Failed to update status'); }
    finally { setUpdatingStatus(null); }
  };

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 400 }}>Orders</h1>
        <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '4px' }}>{total} total orders</p>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button onClick={() => { setStatusFilter(''); setPage(1); }} style={{ padding: '8px 16px', border: `1px solid ${!statusFilter ? 'var(--gold)' : 'var(--border)'}`, background: !statusFilter ? 'rgba(201,168,76,0.1)' : 'transparent', color: !statusFilter ? 'var(--gold)' : 'var(--white-dim)', fontSize: '11px', cursor: 'pointer', letterSpacing: '0.1em' }}>ALL</button>
        {STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            style={{ padding: '8px 16px', border: `1px solid ${statusFilter === s ? STATUS_COLORS[s] : 'var(--border)'}`, background: statusFilter === s ? `${STATUS_COLORS[s]}18` : 'transparent', color: statusFilter === s ? STATUS_COLORS[s] : 'var(--white-dim)', fontSize: '11px', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {s}
          </button>
        ))}
      </div>

      <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)' }}>
        {loading ? (
          <div style={{ padding: '32px' }}>{[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: '60px', marginBottom: '8px' }} />)}</div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--white-dim)' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '22px' }}>No orders found</p>
          </div>
        ) : orders.map(order => (
          <div key={order._id} style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
            <button onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
              style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto auto', gap: '16px', alignItems: 'center', padding: '16px 20px', background: 'none', color: 'var(--white)', cursor: 'pointer', textAlign: 'left' }}>
              <div>
                <p style={{ fontSize: '13px', color: 'var(--gold)', fontWeight: 600 }}>{order.orderNumber}</p>
                <p style={{ fontSize: '11px', color: 'var(--white-dim)', marginTop: '2px' }}>{new Date(order.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <p style={{ fontSize: '13px' }}>{order.user?.name}</p>
                <p style={{ fontSize: '11px', color: 'var(--white-dim)' }}>{order.user?.email}</p>
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--gold)' }}>${order.totalPrice}</span>
              <span style={{ fontSize: '10px', padding: '3px 10px', border: `1px solid ${STATUS_COLORS[order.paymentStatus === 'paid' ? 'delivered' : 'pending']}`, color: order.paymentStatus === 'paid' ? '#10b981' : '#f59e0b', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {order.paymentStatus}
              </span>
              <span style={{ fontSize: '10px', padding: '3px 10px', border: `1px solid ${STATUS_COLORS[order.orderStatus]}`, color: STATUS_COLORS[order.orderStatus], letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {order.orderStatus}
              </span>
              {expandedOrder === order._id ? <ChevronUp size={16} color="var(--white-dim)" /> : <ChevronDown size={16} color="var(--white-dim)" />}
            </button>

            {expandedOrder === order._id && (
              <div style={{ padding: '20px 20px 24px', borderTop: '1px solid var(--border)', background: 'rgba(201,168,76,0.02)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '20px' }}>
                  {/* Items */}
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', marginBottom: '10px' }}>ITEMS</p>
                    {order.items.map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                        <div style={{ width: '40px', height: '40px', background: 'var(--dark-3)', flexShrink: 0, overflow: 'hidden' }}>
                          {item.image && <img src={item.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        </div>
                        <div>
                          <p style={{ fontSize: '12px' }}>{item.name}</p>
                          <p style={{ fontSize: '11px', color: 'var(--white-dim)' }}>Qty: {item.quantity} · ${item.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Shipping */}
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', marginBottom: '10px' }}>SHIP TO</p>
                    <p style={{ fontSize: '12px', lineHeight: 1.8 }}>
                      {order.shippingAddress?.fullName}<br />
                      {order.shippingAddress?.street}<br />
                      {order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.zipCode}<br />
                      {order.shippingAddress?.country}
                    </p>
                  </div>

                  {/* Update Status */}
                  <div>
                    <p style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', marginBottom: '10px' }}>UPDATE STATUS</p>
                    <select defaultValue={order.orderStatus} id={`status-${order._id}`}
                      style={{ width: '100%', padding: '8px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '12px', marginBottom: '8px', cursor: 'pointer' }}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                    <input value={trackingInputs[order._id] || order.trackingNumber || ''}
                      onChange={e => setTrackingInputs(t => ({ ...t, [order._id]: e.target.value }))}
                      placeholder="Tracking number (optional)"
                      style={{ width: '100%', padding: '8px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '12px', marginBottom: '10px' }} />
                    <button onClick={() => {
                      const sel = document.getElementById(`status-${order._id}`);
                      handleStatusUpdate(order._id, sel.value);
                    }} disabled={updatingStatus === order._id}
                      style={{ width: '100%', background: 'var(--gold)', color: 'var(--black)', padding: '10px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', cursor: updatingStatus === order._id ? 'not-allowed' : 'pointer', opacity: updatingStatus === order._id ? 0.7 : 1 }}>
                      {updatingStatus === order._id ? 'UPDATING...' : 'UPDATE'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
          {[...Array(pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              style={{ width: '36px', height: '36px', border: `1px solid ${page === i + 1 ? 'var(--gold)' : 'var(--border)'}`, background: page === i + 1 ? 'var(--gold)' : 'transparent', color: page === i + 1 ? 'var(--black)' : 'var(--white)', fontSize: '12px', cursor: 'pointer' }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
